/**
 * Cliente del flujo de eventos del realm staff (004 · T048 · US4; FR-014a, FR-014b;
 * `contracts/staff-vpp.md` §6): `GET /api/v1/staff/events`.
 *
 * **No confundir con `./events.ts`**, que es un bus de `CustomEvent` del navegador para traducir
 * códigos de error en navegación. Esto es el canal en vivo del servidor, que no existía.
 *
 * ## Por qué `fetch` + `ReadableStream` y nunca `EventSource`
 *
 * La ruta es **autenticada** como cualquier otra del realm (wiring §0.5): exige la cookie de sesión
 * y la cabecera firmada `X-Fermin-Device`. `EventSource` no admite cabeceras, así que queda
 * descartado; es el mismo motivo por el que el marcador consume su canal de respaldo con `fetch`.
 *
 * ## Qué garantiza
 *
 * - Cada mensaje se valida en runtime con `StaffStreamMessageSchema` de `@fermin/contracts`. Un
 *   mensaje desconocido o mal formado **se descarta en silencio** y el stream sigue: un evento roto
 *   no puede tumbar una marcación en curso.
 * - `: ping` y líneas vacías se ignoran; los eventos se separan por `\n\n`, como manda SSE.
 * - **Reconexión con backoff** mientras el llamador no cierre: el servidor corta el stream por
 *   inactividad (`queue_wait_heartbeat_timeout_ms × 3`) y eso es normal, no un error.
 * - El stream **nunca es la única vía** (FR-014a). Si no abre, si se corta o si el navegador no
 *   soporta streams de respuesta, se avisa por `onDegraded` y la pantalla sigue con la señal de
 *   espera (`POST /challenges/{id}/wait`), que lleva exactamente el mismo estado.
 *
 * Nada de lo que entra por aquí lleva tokens, frames, nonces ni coordenadas (FR-018, SC-012), y
 * nada de esto se registra en consola.
 */
import { StaffStreamMessageSchema, type StaffStreamMessage } from '@fermin/contracts';

import { hasKey } from '../device/keys';
import { signRequest } from '../device/sign';

import { getApiBaseUrl, resolvePath } from './client';

/** Ruta del stream, relativa al prefijo del realm. */
export const STAFF_STREAM_PATH = '/events';

/** Backoff de reconexión: 1 s, 2 s, 4 s, tope 8 s. Igual de conservador que el del marcador. */
export const STREAM_RECONNECT_BASE_MS = 1_000;
export const STREAM_RECONNECT_MAX_MS = 8_000;

export interface StaffStreamHandlers {
  /** Un mensaje válido del contrato. */
  onMessage: (message: StaffStreamMessage) => void;
  /**
   * El stream no está disponible (no abrió, se cortó o el navegador no lo soporta): la pantalla
   * debe apoyarse en la señal de espera. Se puede llamar varias veces.
   */
  onDegraded?: (reason: 'UNSUPPORTED' | 'NETWORK' | 'CLOSED') => void;
}

export interface StaffStreamOptions {
  /** Solo pruebas: `fetch` sustituible. */
  fetchImpl?: typeof fetch;
  /** Solo pruebas: espera entre reintentos. */
  delay?: (ms: number) => Promise<void>;
  /** `false` desactiva la reconexión (pruebas). Por defecto `true`. */
  reconnect?: boolean;
}

export interface StaffStreamSubscription {
  /** Cierra el stream y detiene la reconexión. Idempotente. */
  close: () => void;
}

function defaultDelay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Backoff exponencial acotado; el primer reintento no espera nada. */
export function reconnectDelayMs(attempt: number): number {
  if (attempt <= 0) {
    return 0;
  }
  return Math.min(STREAM_RECONNECT_MAX_MS, STREAM_RECONNECT_BASE_MS * 2 ** (attempt - 1));
}

/**
 * Parte el buffer acumulado en eventos SSE completos y devuelve los mensajes válidos junto al resto
 * todavía incompleto. Exportada para poder probarla sin red.
 */
export function parseStreamChunk(buffer: string): { messages: StaffStreamMessage[]; rest: string } {
  const messages: StaffStreamMessage[] = [];
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  for (const part of parts) {
    for (const line of part.split('\n')) {
      // `: ping` (comentario de keep-alive) y cualquier campo que no sea `data:` se ignoran.
      if (!line.startsWith('data:')) {
        continue;
      }
      let payload: unknown;
      try {
        payload = JSON.parse(line.slice('data:'.length).trim());
      } catch {
        continue;
      }
      const parsed = StaffStreamMessageSchema.safeParse(payload);
      if (parsed.success) {
        messages.push(parsed.data);
      }
    }
  }
  return { messages, rest };
}

/**
 * Abre el stream y lo mantiene abierto hasta que se llame a `close()`. Devuelve de inmediato: la
 * lectura vive en una promesa propia.
 */
export function openStaffStream(
  handlers: StaffStreamHandlers,
  options: StaffStreamOptions = {},
): StaffStreamSubscription {
  const delay = options.delay ?? defaultDelay;
  const reconnect = options.reconnect ?? true;

  let closed = false;
  let controller: AbortController | null = null;

  // Entorno sin `fetch` (una WebView vieja, o el jsdom de una prueba que no lo necesita): se degrada
  // sin ruido. La señal de espera sigue funcionando por el cliente HTTP normal.
  if (options.fetchImpl === undefined && typeof globalThis.fetch !== 'function') {
    handlers.onDegraded?.('UNSUPPORTED');
    return { close: () => {} };
  }
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  const degrade = (reason: 'UNSUPPORTED' | 'NETWORK' | 'CLOSED'): void => {
    if (!closed) {
      handlers.onDegraded?.(reason);
    }
  };

  const connectOnce = async (): Promise<void> => {
    const path = resolvePath(STAFF_STREAM_PATH);
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (await hasKey()) {
      headers['X-Fermin-Device'] = await signRequest({ method: 'GET', path });
    }
    controller = new AbortController();

    const response = await fetchImpl(`${getApiBaseUrl()}${path}`, {
      method: 'GET',
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok || response.body === null) {
      // Un 4xx aquí no puede cambiar de pantalla por su cuenta: de eso ya se encarga `client.ts`
      // en la siguiente petición del ciclo (es la que manda). Aquí solo se degrada.
      degrade(response.body === null ? 'UNSUPPORTED' : 'NETWORK');
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) {
        degrade('CLOSED');
        return;
      }
      buffer += decoder.decode(value, { stream: true });
      const { messages, rest } = parseStreamChunk(buffer);
      buffer = rest;
      for (const message of messages) {
        if (closed) {
          return;
        }
        handlers.onMessage(message);
      }
    }
  };

  const run = async (): Promise<void> => {
    let attempt = 0;
    while (!closed) {
      try {
        await connectOnce();
        attempt = 0;
      } catch {
        // Aborto propio o red caída: no se distingue, y la reacción es la misma.
        degrade('NETWORK');
        attempt += 1;
      }
      if (!reconnect || closed) {
        return;
      }
      await delay(reconnectDelayMs(attempt === 0 ? 1 : attempt));
    }
  };

  void run();

  return {
    close: () => {
      if (closed) {
        return;
      }
      closed = true;
      controller?.abort();
    },
  };
}
